import type { Playbook } from "@workspace/api-client-react";
import { Repeat2, ArrowRight, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { PlaybookTitle } from "@/components/playbook-title";
import { getPlaybookImage } from "@/lib/card-image";
import { formatUnfoldCountLabel } from "@/lib/playbook-utils";

/**
 * Shared inner content for a playbook/media card, rendered on a full-bleed
 * photo background with a legibility scrim. Used by the library (surf + grid)
 * and the home/dashboard media cards so they stay visually identical.
 *
 * Place inside a `group relative overflow-hidden` card wrapper.
 */
export function PlaybookCardBody({
  playbook,
  isActive = false,
  hideTitle = false,
}: {
  playbook: Playbook;
  isActive?: boolean;
  hideTitle?: boolean;
}) {
  const image = getPlaybookImage(playbook);

  return (
    <>
      {/* Full-bleed photo background */}
      <img
        src={image}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Scrim keeps the title readable over any photo */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/15" />

      <div className="relative min-w-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
            <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
          </div>
          <span className="text-[10px] leading-[15px] font-semibold uppercase tracking-widest bg-white/15 backdrop-blur-sm text-white/85 border border-white/15 px-2 py-0.5 rounded-full shrink-0 max-w-[min(100%,7.5rem)] truncate">
            {playbook.category}
          </span>
        </div>
        {!hideTitle && (
          <PlaybookTitle
            title={playbook.title}
            variant="card"
            className="text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]"
          />
        )}
      </div>

      <div className="relative flex items-center justify-between gap-2 min-w-0 mt-auto">
        <span className="flex items-center gap-2 min-w-0 text-xs text-white/80 font-medium">
          {playbook.isCustom && (
            <span
              className={cn(
                "shrink-0 rounded-lg px-1.5 py-0.5 text-[9px] leading-3 font-bold uppercase tracking-wider backdrop-blur-sm",
                playbook.canDelete
                  ? "bg-primary/80 text-primary-foreground"
                  : "bg-white/20 text-white",
              )}
            >
              {playbook.canDelete ? "Yours" : "Community"}
            </span>
          )}
          <span className="flex items-center gap-1 shrink-0">
            <Repeat2 className="w-3 h-3" />
            {formatUnfoldCountLabel(playbook)}
          </span>
        </span>
        <div
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm border",
            isActive
              ? "bg-primary text-white border-primary/60"
              : "bg-white/15 text-white border-white/20 group-hover:bg-white/30",
          )}
        >
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </>
  );
}
